import React, { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Close } from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { colors } from '../theme/theme';
import {
  AdminUser,
  CREATE_USER_PLATFORMS,
  PlatformOption,
  basePlatformSource,
  buildCreateUserPlatforms,
  getAvatarColor,
  getUserInitials,
} from '../utils/userHelpers';
import { filterToggleSx } from './AdvancedFiltersPopover';
import {
  barePhone,
  formatPhoneInput,
  validatePhoneNumber as validatePhoneField,
} from '../utils/phoneValidation';
import { isStudioScopedRole } from '../utils/roles';
import BranchSelect from './BranchSelect';
import { useMyLocations } from '../hooks/useMyLocations';

interface UserFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user?: AdminUser | null;
}

const GENDER_OPTIONS = ['Female', 'Male', 'Other'] as const;

// A platform other than SweatSocial's own channels is a studio's whitelabel app.
const hasWhitelabelApp = (platform: string) =>
  platform !== 'mobile_app' && platform !== 'website' && platform !== 'sweatsocial';

const UserFormDialog: React.FC<UserFormDialogProps> = ({ open, onClose, onSuccess, user }) => {
  const { admin } = useAuth();
  // The platform chooser is a super-admin-only concept. A studio owner can only
  // create members under their OWN studio, so we lock the platform to theirs.
  const isStudioRole = isStudioScopedRole(admin?.role);
  // Under "All locations" a NEW member has no implied home: ask which
  // location, and pin the create to it (`__all__` is refused for writes).
  const { isAllLocations, byId } = useMyLocations();
  const mustPickBranch = isStudioRole && isAllLocations && !user;
  const [branchForNew, setBranchForNew] = useState<string>('');
  const branchLoc = mustPickBranch && branchForNew ? byId(branchForNew) : undefined;
  const ownPlatform = (admin?.studio as any)?.whitelabelConfig?.appSource || 'mobile_app';
  // A studio without its own whitelabel app takes bookings through SweatSocial, so
  // its members are SweatSocial users (marketable). Members of a studio WITH its own
  // app belong to that app and aren't.
  // (The chosen location's app, when one was picked under "All locations".)
  const studioHasOwnApp = hasWhitelabelApp(
    (branchLoc?.whitelabelConfig ? branchLoc.whitelabelConfig.appSource : ownPlatform) || 'mobile_app',
  );
  // A booking website makes it a whitelabel studio too: its members are its
  // own, never SweatSocial-marketable (backend studioOwnsItsMembers).
  const studioOwnsMembers =
    studioHasOwnApp ||
    ((branchLoc ?? (admin?.studio as any))?.whitelabelConfig?.enableBookingWebsite === true);
  const isEditMode = Boolean(user);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [age, setAge] = useState('');
  const [city, setCity] = useState('');
  const [gender, setGender] = useState<string>('Female');
  const [platform, setPlatform] = useState<string>('mobile_app');
  const [isMarketable, setIsMarketable] = useState(true);
  // Creating on a non-whitelabel platform: always marketable, so the switch is locked on.
  const lockedMarketable = !isEditMode && !hasWhitelabelApp(platform);
  const [platforms, setPlatforms] = useState<PlatformOption[]>([...CREATE_USER_PLATFORMS]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    setBranchForNew('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setFieldErrors({});
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
      setPhoneNumber(barePhone(user.phoneNumber || ''));
      setAge(user.age?.toString() || '');
      setCity(user.city || '');
      setGender(user.gender || 'Female');
      // Detach markers aren't selectable platforms — fall back so the Select
      // has a valid value; saving reassigns a real registrationSource.
      const src = user.registrationSource || 'mobile_app';
      // `chakra_web` is not a selectable option — show the brand so the Select
      // has a valid value. The send condition below compares BASE sources, so
      // leaving it alone does not quietly rewrite the member to 'chakra' and
      // lose the record that they came through the website.
      setPlatform(
        src.startsWith('removed_by_')
          ? (isStudioRole ? ownPlatform : 'mobile_app')
          : basePlatformSource(src),
      );
      setIsMarketable(user.isMarketable ?? true);
    } else {
      setName('');
      setEmail('');
      setPhoneNumber('');
      setAge('');
      setCity('');
      setGender('Female');
      // Studio owners always create under their own studio's platform.
      setPlatform(isStudioRole ? ownPlatform : 'mobile_app');
      setIsMarketable(true);
    }
  }, [open, user, isStudioRole, ownPlatform]);

  // Platform options are dynamic: SweatSocial + every studio that has a whitelabel
  // app enabled (whitelabelConfig.appSource) + Website. So enabling a new whitelabel
  // makes it appear here automatically. Only needed for the super-admin chooser.
  useEffect(() => {
    if (!open || isStudioRole) return;
    let cancelled = false;
    (async () => {
      try {
        const res: any = await apiService.getStudios({ pageSize: 1000 });
        const studios = res?.studios || res?.data || [];
        if (!cancelled) setPlatforms(buildCreateUserPlatforms(studios));
      } catch {
        // Keep the seeded CREATE_USER_PLATFORMS list on failure.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, isStudioRole]);

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = 'Full name is required';
    const phoneError = validatePhoneField(phoneNumber);
    if (phoneError) next.phoneNumber = phoneError;
    const trimmedEmail = email.trim();
    const hadEmail = Boolean(user?.email?.trim());
    if (!isEditMode && !trimmedEmail) {
      next.email = 'Email is required';
    } else if (isEditMode && hadEmail && !trimmedEmail) {
      next.email = 'Email cannot be cleared';
    } else if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      next.email = 'Enter a valid email address';
    }
    if (!age.trim()) {
      next.age = 'Age is required';
    } else if (Number.isNaN(Number(age)) || Number(age) > 120) {
      next.age = 'Enter a valid age';
    } else if (Number(age) < 18) {
      next.age = 'User must be at least 18 years old';
    }
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    if (mustPickBranch && !branchForNew) {
      setError('Choose the location this member belongs to.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const trimmedEmail = email.trim();
      const payload = {
        name: name.trim(),
        email:
          isEditMode && !trimmedEmail && !user?.email?.trim()
            ? undefined
            : trimmedEmail || undefined,
        phoneNumber: phoneNumber.trim(),
        age: Number(age),
        city: city.trim() || undefined,
        gender: gender || undefined,
        // Studio roles don't choose a platform: the backend derives it from the
        // studio on create and ignores it on edit. Sending the studio's own app
        // source anyway made the request fail validation whenever that value
        // wasn't in the backend's fixed list (or was 'sweatsocial').
        // Super-admin: always on create; on edit only when the platform actually
        // changed. Re-sending an untouched value failed validation for users whose
        // stored source isn't creatable ('studio_web', 'admin') and blocked saving
        // any other field. A detached user (removed_by_*) still gets a real source
        // written on save, as before, because the form shows the fallback for them.
        ...(!isStudioRole && (!isEditMode || platform !== basePlatformSource(user?.registrationSource || 'mobile_app'))
          ? { registrationSource: platform }
          : {}),
        // Super-admin: a new user on a whitelabel platform uses the switch (which
        // defaults off); on any non-whitelabel platform a new user is always
        // marketable. Editing uses the switch as-is.
        // Studio roles never see the switch: on create it follows whether the
        // studio has its own whitelabel app, and on edit it's omitted so the
        // stored value is left untouched.
        ...(!isStudioRole
          ? { isMarketable: isEditMode || hasWhitelabelApp(platform) ? isMarketable : true }
          : isEditMode
            ? {}
            : { isMarketable: !studioOwnsMembers }),
      };

      if (isEditMode && user) {
        // Under "All locations", send the edit to a location this member is
        // linked to that the caller also holds — the server checks the link.
        const memberPin = isAllLocations
          ? ((user as any).associatedStudios ?? [])
              .map((a: any) => String(a?.studio?._id ?? a?.studio ?? ''))
              .find((sid: string) => !!byId(sid))
          : undefined;
        await apiService.updateUser(user._id, payload, memberPin);
      } else {
        await apiService.createUser(payload, mustPickBranch ? branchForNew : undefined);
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        `Failed to ${isEditMode ? 'update' : 'create'} user`;
      setError(Array.isArray(message) ? message.join(', ') : String(message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between">
          <Box display="flex" gap={1.5} alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: `${colors.primary}15`,
                color: colors.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Add fontSize="small" />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {isEditMode ? 'Edit user' : 'Create user'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isEditMode ? 'Update account details' : 'Add a new account to your platform'}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {mustPickBranch && (
          <Box sx={{ mb: 2.5 }}>
            <BranchSelect
              value={branchForNew}
              onChange={(v) => setBranchForNew(v || '')}
              minRole="manager"
            />
          </Box>
        )}

        <Box
          sx={{
            mb: 2.5,
            p: 2,
            borderRadius: 2,
            bgcolor: colors.backgroundTertiary,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Avatar
            src={user?.image}
            sx={{
              width: 56,
              height: 56,
              bgcolor: getAvatarColor(name),
              fontWeight: 700,
            }}
          >
            {getUserInitials(name)}
          </Avatar>
          <Box>
            <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">
              AVATAR
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEditMode
                ? 'Upload available from the user profile after saving changes.'
                : 'Auto-generated from name. Upload available after creation.'}
            </Typography>
          </Box>
        </Box>

        <TextField
          fullWidth
          label="Full Name *"
          placeholder="e.g. Akshata K"
          value={name}
          onChange={e => setName(e.target.value)}
          error={Boolean(fieldErrors.name)}
          helperText={fieldErrors.name}
          sx={{ mb: 2 }}
        />

        <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2} mb={2}>
          <TextField
            label={isEditMode ? 'Email' : 'Email *'}
            placeholder="name@email.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            error={Boolean(fieldErrors.email)}
            helperText={fieldErrors.email}
          />
          <TextField
            label="Phone *"
            placeholder="9876543210"
            value={phoneNumber}
            onChange={e => setPhoneNumber(formatPhoneInput(e.target.value))}
            error={Boolean(fieldErrors.phoneNumber)}
            helperText={fieldErrors.phoneNumber}
            inputProps={{ maxLength: 10, inputMode: 'numeric' }}
            InputProps={{
              startAdornment: (
                <Typography variant="body2" sx={{ mr: 0.5, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                  +91
                </Typography>
              ),
            }}
          />
        </Box>

        <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2} mb={2}>
          <TextField
            label="Age *"
            placeholder="25"
            value={age}
            onChange={e => setAge(e.target.value)}
            error={Boolean(fieldErrors.age)}
            helperText={fieldErrors.age || 'Must be 18 or above'}
          />
          <TextField
            label="City (optional)"
            placeholder="Bengaluru"
            value={city}
            onChange={e => setCity(e.target.value)}
          />
        </Box>

        <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1}>
          Gender
        </Typography>
        <Box display="flex" gap={1} mb={2.5} flexWrap="wrap">
          {GENDER_OPTIONS.map(option => (
            <Button
              key={option}
              variant="outlined"
              size="small"
              onClick={() => setGender(option)}
              sx={filterToggleSx(gender === option)}
            >
              {option}
            </Button>
          ))}
        </Box>

        {/* Platform chooser is super-admin only. Studio owners create members
            under their own studio, so the platform is locked + hidden. */}
        {!isStudioRole && (
          <>
            <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1}>
              Platform
            </Typography>
            <Box display="flex" gap={1} mb={2.5} flexWrap="wrap">
              {platforms.map(option => (
                <Button
                  key={option.value}
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setPlatform(option.value);
                    // New user: the default follows the platform (whitelabel → off,
                    // otherwise on). The switch can still be changed for a whitelabel.
                    if (!isEditMode) setIsMarketable(!hasWhitelabelApp(option.value));
                  }}
                  sx={filterToggleSx(platform === option.value)}
                >
                  {option.label}
                </Button>
              ))}
            </Box>
          </>
        )}

        {/* Marketable is a SweatSocial marketing field — only super-admins manage it. */}
        {!isStudioRole && (
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              border: `1px solid ${colors.divider}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <Typography variant="body2" fontWeight={700}>
                Marketable
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {lockedMarketable
                  ? 'Always on for SweatSocial platforms.'
                  : 'User consents to marketing communications.'}
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={lockedMarketable ? true : isMarketable}
                  disabled={lockedMarketable}
                  onChange={e => setIsMarketable(e.target.checked)}
                  color="success"
                />
              }
              label=""
              sx={{ m: 0 }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || (mustPickBranch && !branchForNew)}
          sx={{ textTransform: 'none', px: 3 }}
        >
          {loading ? 'Saving…' : isEditMode ? 'Save changes' : 'Create user'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserFormDialog;
